import React from 'react';
import { Avatar, Box, Menu, Text, UnstyledButton, rem } from '@mantine/core';
import { IconChevronRight, IconLogout } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useIsPhone } from '@/hooks/use.responsive';
import { DURATION, EASE_OUT } from '@/theme/motion';
import { useAppSelector } from '@/store/store.hooks';
import { selectSessionUser } from '@/store/auth/auth.selectors';
import { useLogoutMutation } from '@/store/api/auth/auth.api';
import { ROLE_LABEL, initialsOf } from '@/function/session/role.labels';
import notify from '@/lib/notify/notify';
import { PROFILE_NODE } from './config/navigation.tree';

type Props = {
    expanded: boolean;
    onNavigate?: () => void;
};

const ShellNavbarProfile: React.FC<Props> = ({ expanded, onNavigate }) => {
    const navigate = useNavigate();
    const isPhone = useIsPhone();
    const me = useAppSelector(selectSessionUser);
    const [logout, logoutState] = useLogoutMutation();

    const user = {
        name: me?.displayName || 'Non connesso',
        role: me ? ROLE_LABEL[me.role] : 'Nessuna sessione',
        initials: me ? initialsOf(me.firstname, me.lastname, me.email) : '—',
    };

    const leave = async () => {
        try {
            await logout().unwrap();
        } catch {
        } finally {
            notify.success('Sessione chiusa', 'A presto.');
            navigate('/auth/login', { replace: true });
        }
    };

    const go = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    return (
        <Menu position={isPhone ? 'top-start' : 'right-end'} withArrow offset={12} width={232} withinPortal shadow="lg">
            <Menu.Target>
                <UnstyledButton
                    aria-label={`Profilo di ${user.name}, apri il menu`}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: rem(10),
                        width: '100%',
                        padding: rem(8),
                        borderRadius: 'var(--mantine-radius-md)',
                        justifyContent: expanded ? 'flex-start' : 'center',
                        border: '1px solid transparent',
                        transition: 'background-color 160ms ease, border-color 160ms ease',
                    }}
                    onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = 'var(--bd-surface-hover)'; event.currentTarget.style.borderColor = 'var(--bd-border)'; }}
                    onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = 'transparent'; event.currentTarget.style.borderColor = 'transparent'; }}
                >
                    <Avatar radius="xl" size={34} color="brand" variant="light" style={{ flexShrink: 0 }}>
                        {user.initials}
                    </Avatar>

                    <AnimatePresence initial={false}>
                        {expanded ? (
                            <motion.div
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -6 }}
                                transition={{ duration: DURATION.fast, ease: EASE_OUT }}
                                style={{ display: 'flex', alignItems: 'center', gap: rem(6), flex: 1, minWidth: 0, overflow: 'hidden' }}
                            >
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Text size="sm" fw={600} truncate style={{ color: 'var(--bd-text-strong)' }}>{user.name}</Text>
                                    <Text size="xs" c="dimmed" truncate>{user.role}</Text>
                                </Box>
                                <IconChevronRight size={14} opacity={0.5} style={{ flexShrink: 0 }} />
                            </motion.div>
                        ) : null}
                    </AnimatePresence>
                </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Label>
                    <Text size="xs" fw={650} style={{ color: 'var(--bd-text-strong)' }}>{user.name}</Text>
                    <Text size="xs" c="dimmed">{user.role}</Text>
                </Menu.Label>
                <Menu.Divider />
                {(PROFILE_NODE.children ?? []).map((child) => {
                    const Icon = child.icon;
                    return (
                        <Menu.Item key={child.id} leftSection={<Icon size={16} stroke={1.7} />} onClick={() => go(child.path)}>
                            {child.label}
                        </Menu.Item>
                    );
                })}
                <Menu.Divider />
                <Menu.Item color="danger" leftSection={<IconLogout size={16} stroke={1.7} />} disabled={logoutState.isLoading} onClick={() => { void leave(); }}>
                    Esci
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
};

export default ShellNavbarProfile;
